// src/app/time-attendance/scheduling/services/scheduling-sync.service.ts

import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

import {
  ApiPagedResponse,
  ApiResponse,
  ScheduleSyncProgress,
  YywwListItem,
} from '../models/scheduling-sync.model';

@Injectable({ providedIn: 'root' })
export class SchedulingSyncService {
  private readonly baseUrl = environment.apiBaseUrl;

  private readonly syncStateSubject = new BehaviorSubject<ScheduleSyncProgress>({
    status: 'idle',
    yywwId: null,
    totalCalls: 0,
    completedCalls: 0,
    lastError: null,
  });

  readonly syncState$ = this.syncStateSubject.asObservable();

  private running = false;

  constructor(private readonly http: HttpClient) {
    this.restoreState();
  }

  /* =============================
     YYWW search (paged)
     ============================= */
  searchYywws(opts: {
    searchTerm: string;
    pageNumber?: number;
    pageSize?: number;
  }): Observable<ApiPagedResponse<YywwListItem>> {
    const params = new HttpParams()
      .set('searchTerm', String(opts.searchTerm ?? '').trim())
      .set('pageNumber', String(opts.pageNumber ?? 1))
      .set('pageSize', String(opts.pageSize ?? 6));

    return this.http.get<ApiPagedResponse<YywwListItem>>(`${this.baseUrl}/api/yywws`, { params });
  }

  /* =============================
     Sync runner (week only)
     ============================= */
  startSync(input: { yywwId: number }): void {
    const yywwId = Number(input.yywwId);

    if (!Number.isFinite(yywwId)) {
      this.setState({
        status: 'error',
        lastError: 'Select a week.',
      });
      return;
    }

    if (this.running) {
      this.setState({
        lastError: 'A sync is already running.',
      });
      return;
    }

    this.running = true;

    this.setState({
      status: 'running',
      startedAt: new Date().toISOString(),
      finishedAt: undefined,
      yywwId,
      totalCalls: 1,
      completedCalls: 0,
      lastError: null,
    });

    void this.runSync(yywwId);
  }

  resetSyncState(): void {
    this.running = false;
    this.setState({
      status: 'idle',
      startedAt: undefined,
      finishedAt: undefined,
      yywwId: null,
      totalCalls: 0,
      completedCalls: 0,
      lastError: null,
    });
  }

  private async runSync(yywwId: number): Promise<void> {
    try {
      await this.assignFromDefault(yywwId).toPromise();

      this.setState({
        status: 'success',
        finishedAt: new Date().toISOString(),
        completedCalls: 1,
      });
    } catch (e: any) {
      const message =
        (typeof e?.error?.message === 'string' && e.error.message) ||
        (typeof e?.message === 'string' && e.message) ||
        'Sync failed.';

      this.setState({
        status: 'error',
        finishedAt: new Date().toISOString(),
        lastError: message,
      });
    } finally {
      this.running = false;
    }
  }

  private assignFromDefault(yywwId: number): Observable<ApiResponse<unknown>> {
    return this.http.post<ApiResponse<unknown>>(
      `${this.baseUrl}/api/schedule-groups/assign-from-default`,
      { YYWWId: yywwId },
    );
  }

  /* =============================
     State persistence
     ============================= */
  private setState(patch: Partial<ScheduleSyncProgress>): void {
    const next: ScheduleSyncProgress = {
      ...this.syncStateSubject.value,
      ...patch,
    };

    this.syncStateSubject.next(next);

    try {
      sessionStorage.setItem('scheduleSyncState', JSON.stringify(next));
    } catch {
      // ignore storage errors
    }
  }

  private restoreState(): void {
    try {
      const raw = sessionStorage.getItem('scheduleSyncState');
      if (!raw) return;

      const parsed = JSON.parse(raw) as ScheduleSyncProgress | null;
      if (!parsed) return;

      if (parsed.status === 'running') {
        this.syncStateSubject.next({
          ...parsed,
          status: 'error',
          lastError: 'Sync was interrupted (page refresh). Please run again.',
        });
        return;
      }

      this.syncStateSubject.next(parsed);
    } catch {
      // ignore
    }
  }
}